import type {
  EngineMarkets,
  MediaTypeChar,
  SearchFilterState,
  SearchPayload,
  SearchQueryPayload,
  StatusChar,
} from '../types'
import { encodeCriteriaString } from './criteriaString'
import { mapSearchMetaOptionsToQuery } from './searchMetaOptions'

const toDateOrNull = (value: string | null) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const toIsoStringOrNull = (
  value: Date | string | null | undefined,
): string | null => {
  if (value == null) return null

  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export const mapServerToState = (
  serverQuery: Omit<SearchQueryPayload, 'options'>,
): SearchFilterState => {
  const {
    collectionIncludes,
    containsStringIncludes,
    dateIncludes,
    singleIncludes,
    specificObjectIncludes,
    specificObjectMultiples,
    xMetaDataIncludes,
  } = serverQuery.customSearchOptions

  const { include: providerInclude, exclude: providerExclude } =
    specificObjectMultiples?.provider ?? { include: [], exclude: [] }

  function getXMetaDataValue(valueCode: string) {
    return xMetaDataIncludes.find(
      (i) => i.valueCode.toUpperCase() === valueCode.toUpperCase(),
    )
  }

  function getStartsWith(valueCode: string) {
    return xMetaDataIncludes
      .filter(
        (i) =>
          i.valueCode.slice(0, valueCode.length).toUpperCase() ===
          valueCode.toUpperCase(),
      )
      .map((i) => ({
        label: i.selectedValue,
        valueCode: i.valueCode,
      }))
  }

  function getXMetaDataBoolean(valueCode: string) {
    return (
      xMetaDataIncludes
        .find((i) => i.valueCode === valueCode.toUpperCase())
        ?.selectedValue.toUpperCase() === 'YES'
    )
  }

  return {
    searchText: serverQuery.searchText,
    mediaType: {
      document: singleIncludes.mediaType.includes('D'),
      image: singleIncludes.mediaType.includes('I'),
      video: singleIncludes.mediaType.includes('V'),
    },
    industries: Object.fromEntries(
      collectionIncludes.engineMarkets.map((v) => [v, true]),
    ),
    status: {
      current: singleIncludes.status.includes('C'),
      nonCurrent: singleIncludes.status.includes('N'),
    },
    documentInformation: {
      mediaNumber: containsStringIncludes.mediaNumber,
      categories: collectionIncludes.categories,
      productTypes: collectionIncludes.productTypes,
      subIndustries: collectionIncludes.subIndustries,
    },
    productInformation: {
      engineModels: collectionIncludes.engineModels,
      performanceNo: specificObjectIncludes.performanceNo,
      revisionNo: specificObjectIncludes.revisionNo,
      emissionTiers: collectionIncludes.emissionTiers,
      targetRegions: collectionIncludes.regions,
      duties: collectionIncludes.duties,
      sources: collectionIncludes.sources,
      fuelTypes: collectionIncludes.fuelTypes,
      frequencies: collectionIncludes.frequencies,
      rpms: collectionIncludes.rpms,
      voltages: collectionIncludes.voltages,
      applications: collectionIncludes.applications,
      ratings: {
        kva: specificObjectIncludes.ratingKva,
        bkw: specificObjectIncludes.ratingBkw,
        bhp: specificObjectIncludes.ratingBhp,
        ekw: specificObjectIncludes.ratingEkw,
        mhp: specificObjectIncludes.ratingMhp,
      },
      countryOfUse: getStartsWith('FUI'),
      GERP: getXMetaDataBoolean('GERP'),
      CSQ: getXMetaDataBoolean('CSQTRUE'),
      TIP: getXMetaDataBoolean('TIP'),
      excitation: getStartsWith('EXCIT'),
      generatorArrangement: getXMetaDataValue('GENARGTXT')?.selectedValue ?? '',
      configurableSalesModel: getXMetaDataValue('CSMTEXT')?.selectedValue ?? '',
      featureCode: getXMetaDataValue('FCTEXT')?.selectedValue ?? '',
      electricPowerSpecSizer: getXMetaDataBoolean('EPSSTRUE'),
      catDotComProductTechnicalInformation: getXMetaDataBoolean('CATTRUE'),
      appliesTo: getXMetaDataValue('Genset')?.selectedValue ?? '',
      powerDotCatDotCom: getXMetaDataBoolean('VSPTRUE'),
    },
    advancedTextSearching: {
      title: containsStringIncludes.title,
      keyword: containsStringIncludes.keywords,
      longDescription: containsStringIncludes.longDescription,
    },
    mediaDetails: {
      securedFrom: toDateOrNull(dateIncludes.fromSecureDate),
      securedTo: toDateOrNull(dateIncludes.toSecureDate),
      modifiedFrom: toDateOrNull(dateIncludes.fromDateModified),
      modifiedTo: toDateOrNull(dateIncludes.toDateModified),
      languages: collectionIncludes.languages,
      provider: providerExclude.length > 0 ? providerExclude : providerInclude,
      providerMode: providerExclude.length > 0 ? 'exclude' : 'include',
      affiliationCode: specificObjectIncludes.affiliationCodes,
      productId: specificObjectIncludes.productIds,
    },
  }
}

export const MapStateToFlat = (state: SearchFilterState) => {
  const {
    searchText,
    mediaType,
    status,
    industries,
    documentInformation: {
      mediaNumber,
      categories,
      productTypes,
      subIndustries,
    },
    mediaDetails: {
      affiliationCode,
      languages,
      modifiedFrom,
      modifiedTo,
      productId,
      provider,
      providerMode,
      securedFrom,
      securedTo,
    },
    productInformation: {
      applications,
      CSQ,
      GERP,
      TIP,
      appliesTo,
      catDotComProductTechnicalInformation,
      configurableSalesModel,
      countryOfUse,
      duties,
      electricPowerSpecSizer,
      emissionTiers,
      engineModels,
      excitation,
      featureCode,
      frequencies,
      fuelTypes,
      generatorArrangement,
      performanceNo,
      powerDotCatDotCom,
      ratings,
      revisionNo,
      rpms,
      sources,
      targetRegions,
      voltages,
    },
    advancedTextSearching: { keyword, longDescription, title },
  } = state
  return {
    searchText,
    mediaType: mapMediaType(mediaType),
    status: mapStatus(status),
    industries,
    market: mapIndustries(industries),
    mediaNumber,
    category: categories,
    productTypes,
    subIndustries,
    affiliationCode,
    languages,
    modifiedFrom,
    modifiedTo,
    productId,
    provider,
    providerMode,
    securedFrom,
    securedTo,
    applications,
    CSQ,
    GERP,
    TIP,
    appliesTo,
    catDotComProductTechnicalInformation,
    configurableSalesModel,
    countryOfUse,
    duties,
    electricPowerSpecSizer,
    emissionTiers,
    engineModels,
    excitation,
    featureCode,
    frequencies,
    fuelTypes,
    generatorArrangement,
    performanceNo,
    powerDotCatDotCom,
    ratings,
    revisionNo,
    rpms,
    sources,
    targetRegions,
    voltages,
    keyword,
    longDescription,
    title,
  }
}

export const mapStateToQuery = (
  searchState: SearchFilterState,
): Omit<SearchQueryPayload, 'options'> => {
  const {
    searchText,
    mediaType,
    status,
    industries,
    documentInformation: {
      mediaNumber,
      categories,
      productTypes,
      subIndustries,
    },
    mediaDetails: {
      affiliationCode,
      languages,
      modifiedFrom,
      modifiedTo,
      productId,
      provider,
      providerMode,
      securedFrom,
      securedTo,
    },
    advancedTextSearching: { keyword, longDescription, title },
    productInformation: {
      applications,
      CSQ,
      GERP,
      TIP,
      appliesTo,
      catDotComProductTechnicalInformation,
      configurableSalesModel,
      countryOfUse,
      duties,
      electricPowerSpecSizer,
      emissionTiers,
      engineModels,
      excitation,
      featureCode,
      frequencies,
      fuelTypes,
      generatorArrangement,
      performanceNo,
      powerDotCatDotCom,
      ratings: { bhp, bkw, kva, ekw, mhp },
      revisionNo,
      rpms,
      sources,
      targetRegions,
      voltages,
    },
  } = searchState

  const searchQueryParams: Omit<SearchQueryPayload, 'options'> = {
    searchCriteria: '',
    searchText,
    customSearchOptions: {
      singleIncludes: {
        mediaType: mapMediaType(mediaType),
        status: mapStatus(status),
      },
      collectionIncludes: {
        productTypes,
        applications,
        categories,
        duties,
        emissionTiers: emissionTiers,
        engineMarkets: mapIndustries(industries),
        engineModels,
        frequencies,
        fuelTypes,
        languages,
        regions: targetRegions,
        rpms,
        sources,
        subIndustries,
        voltages,
      },
      containsStringIncludes: {
        keywords: keyword,
        longDescription,
        title,
        mediaNumber,
      },
      dateIncludes: {
        fromDateModified: toIsoStringOrNull(modifiedFrom),
        toDateModified: toIsoStringOrNull(modifiedTo),
        fromSecureDate: toIsoStringOrNull(securedFrom),
        toSecureDate: toIsoStringOrNull(securedTo),
      },
      specificObjectIncludes: {
        affiliationCodes: affiliationCode,
        performanceNo,
        productIds: productId,
        ratingBhp: bhp,
        ratingBkw: bkw,
        ratingEkw: ekw,
        ratingKva: kva,
        ratingMhp: mhp,
        revisionNo,
      },
      specificObjectMultiples: {
        provider: {
          include: providerMode === 'include' ? provider : [],
          exclude: providerMode === 'exclude' ? provider : [],
        },
      },
      xMetaDataIncludes: [
        GERP
          ? { type: 'single', selectedValue: 'YES', valueCode: 'GERP' }
          : null,
        CSQ
          ? { type: 'single', selectedValue: 'YES', valueCode: 'CSQTRUE' }
          : null,
        TIP ? { type: 'single', selectedValue: 'YES', valueCode: 'TIP' } : null,
        electricPowerSpecSizer
          ? { type: 'single', selectedValue: 'YES', valueCode: 'EPSSTRUE' }
          : null,
        catDotComProductTechnicalInformation
          ? { type: 'single', selectedValue: 'YES', valueCode: 'CATTRUE' }
          : null,
        powerDotCatDotCom
          ? { type: 'single', selectedValue: 'YES', valueCode: 'VSPTRUE' }
          : null,

        generatorArrangement
          ? {
              type: 'freetextmulti',
              selectedValue: generatorArrangement,
              valueCode: 'GENARGTXT',
            }
          : null,
        featureCode
          ? {
              type: 'freetextmulti',
              selectedValue: featureCode,
              valueCode: 'FCTEXT',
            }
          : null,
        appliesTo
          ? { type: 'radio', selectedValue: appliesTo, valueCode: 'Genset' }
          : null,

        configurableSalesModel
          ? {
              type: 'freetextmulti',
              selectedValue: configurableSalesModel,
              valueCode: 'CSMTEXT',
            }
          : null,

        ...excitation.map((e) => ({
          type: 'multiple',
          selectedValue: e.label,
          valueCode: e.valueCode.toUpperCase(),
        })),

        ...countryOfUse.map((c) => ({
          type: 'multiple',
          selectedValue: c.label,
          valueCode: c.valueCode.toUpperCase(),
        })),
      ].filter((i) => i !== null), //filter out null values
    },
  }
  return searchQueryParams
}

const mapIndustries = (industries: SearchFilterState['industries']) => {
  const mapValue: EngineMarkets[] = []

  Object.entries(industries).forEach(([key, value]) => {
    if (value) mapValue.push(key.toUpperCase())
  })

  return mapValue
}

const mapMediaType = (mediaType: SearchFilterState['mediaType']) => {
  const activeMediaTypes: MediaTypeChar[] = []
  mediaType.document && activeMediaTypes.push('D')
  mediaType.image && activeMediaTypes.push('I')
  mediaType.video && activeMediaTypes.push('V')
  return activeMediaTypes
}

const mapStatus = (status: SearchFilterState['status']) => {
  const statuses: StatusChar[] = []
  status.current && statuses.push('C')
  status.nonCurrent && statuses.push('N')
  return statuses
}

export const mapToQuery = (payload: SearchPayload): SearchQueryPayload => {
  const mappedState = mapStateToQuery(payload)
  const mappedOptions = mapSearchMetaOptionsToQuery(payload.options)
  const searchCriteria = encodeCriteriaString(payload)

  return Object.assign({}, mappedState, {
    options: mappedOptions,
    searchCriteria,
  })
}
